/**
 * 桥梁管理 - 地图控制逻辑
 */
// 注释掉未使用的导入，保留导入以备将来使用
// import L from 'leaflet';
import mapUtils from '@/utils/mapUtils';
import { initLayers } from '@/utils/mapLayerRenderer';
import BridgeEventBus, { BRIDGE_EVENTS } from '@/utils/BridgeEventBus';
import bridgeMapStore from '@/store/bridgeMapStore';

// 初始化地图及相关事件处理
export default {
  // 创建时的钩子函数，用于注册事件监听
  created() {
    // 监听状态重置事件
    BridgeEventBus.$on(BRIDGE_EVENTS.STATE_RESET, this.handleStateReset);
  },
  
  // 销毁前的钩子函数，用于移除事件监听
  beforeDestroy() {
    // 移除状态重置事件监听
    BridgeEventBus.$off(BRIDGE_EVENTS.STATE_RESET, this.handleStateReset);
  },
  computed: {
    // 从store获取位置拾取状态
    isLocationPickingActive() {
      return bridgeMapStore.state.isLocationPicking;
    }
  },
  
  data() {
    // 尝试从localStorage获取上次访问的桥梁位置信息
    let initialCenter = [31.230416, 121.473701]; // 默认上海中心
    
    // 注意：在控制器初始化时不应该直接访问路由，我们使用默认中心点
    // 实际的桥梁位置将在created或mounted钩子中通过BridgeManagement组件设置
    
    return {
      // 地图实例
      map: null,
      // 地图配置
      zoom: 12,
      center: initialCenter, // 使用从缓存获取的中心点或默认值
      tiandituKey: '038937b9b91fd28203af58e3dd9b1396', // 天地图密钥
      mapOptions: {
        zoomControl: false,
        attributionControl: false
      },
      // 图层管理
      layers: [],
      mapLayers: [
        { id: 'base', name: '底图', visible: true },
        { id: 'bridge', name: '桥梁标记', visible: true },
        { id: 'area', name: '区域显示', visible: true }
      ],
      currentMapType: 'satellite', // 默认使用卫星图
      showLabels: true,
      // 鼠标位置坐标
      mouseLat: 0,
      mouseLng: 0,
      // 上一个点击的坐标
      lastClickLat: 0,
      lastClickLng: 0
    };
  },
  
  methods: {
    // 处理状态重置事件
    handleStateReset() {
      console.log('地图控制器响应状态重置事件...');
      
      // 重置地图状态
      if (this.map) {
        // 重置地图中心和缩放级别，但保留地图实例
        this.center = [23.13, 113.27];
        this.zoom = 12;
        
        // 恢复默认视图
        this.map.setView(this.center, this.zoom);
      }
      
      // 重置标记
      this.marker = null;
      
      // 重置鼠标相关状态
      this.mouseLat = 0;
      this.mouseLng = 0;
      this.lastClickLat = 0;
      this.lastClickLng = 0;
      
      console.log('地图控制器状态已重置');
    },
    
    // 初始化地图
    initMap() {
      // 修复Leaflet默认图标问题
      mapUtils.fixDefaultLeafletIcons();
      
      // 初始化图层
      this.initMapLayers();
    },
    
    // 初始化图层
    initMapLayers() {
      // 使用工具函数初始化图层
      this.layers = initLayers(this.currentMapType, this.tiandituKey, this.showLabels);
    },
    
    // 地图加载完成事件
    onMapReady(mapInstance) {
      if (!this.map) {
        this.map = mapInstance;
        console.log('地图加载完成');
        
        // 设置点击事件监听器
        this.map.on('click', this.onMapClick);
        
        // 如果是编辑模式且已有坐标，将地图中心定位到桥梁位置
        if (this.isEdit && this.bridgeForm.latitude && this.bridgeForm.longitude) {
          this.map.setView([this.bridgeForm.latitude, this.bridgeForm.longitude], 15);
          
          // 确保加载区域数据
          if (this.bridgeForm.id) {
            this.loadBridgeAreas(this.bridgeForm.id);
            
            // 缓存桥梁位置信息到localStorage
            this.cacheBridgePosition(this.bridgeForm);
          }
        }
        
        // 刷新地图大小
        setTimeout(() => {
          this.map.invalidateSize();
        }, 200);
      }
    },

    // 缓存桥梁位置信息到localStorage - 使用统一方法
    cacheBridgePosition(bridge) {
      // 调用store中的统一方法
      bridgeMapStore.actions.cacheBridgePosition(bridge);
    },
    
    // 从localStorage获取桥梁位置信息 - 使用统一方法
    getBridgePositionFromCache(bridgeId) {
      // 调用store中的统一方法
      return bridgeMapStore.actions.getBridgePositionFromCache(bridgeId);
    },

    // 地图点击事件处理
    onMapClick(event) {
      try {
        // 参数校验
        if (!event) {
          console.warn("地图点击事件缺少event参数");
          return;
        }
        
        // 从事件对象中安全提取latlng
        let latlng;
        if (event.latlng) {
          // 标准Leaflet事件对象
          latlng = event.latlng;
        } else if (typeof event === 'object' && 'lat' in event && 'lng' in event) {
          // 直接传递坐标对象
          latlng = event;
        } else {
          console.warn("无效的地图点击事件格式", event);
          return;
        }
        
        // 确认坐标对象有效
        if (typeof latlng.lat === 'undefined' || typeof latlng.lng === 'undefined') {
          console.warn("坐标对象缺少lat或lng属性", latlng);
          return;
        }
        
        // 根据当前模式处理点击事件
        if (bridgeMapStore.state.isLocationPicking) {
          // 桥梁位置标记模式
          this.handleLocationPicked(latlng);
        } else if (this.isDrawingActive && this.activeTab === 'area') {
          // 区域绘制模式
          this.handleDrawingClick(latlng);
        }
      } catch (error) {
        console.error('处理地图点击事件失败:', error);
      }
    },
    
    // 处理位置拾取 - 使用统一store管理
    handleLocationPicked(latlng) {
      // 使用store处理位置拾取
      bridgeMapStore.actions.endLocationPicking(latlng);
      
      // 同步本地状态（以确保视图更新）
      this.bridgeForm.latitude = latlng.lat;
      this.bridgeForm.longitude = latlng.lng;
      this.marker = [latlng.lat, latlng.lng];
      // isLocationPickingActive已从store中获取，不需要在这里设置
    },
    
    // 处理区域绘制点击 - 使用统一store方法
    handleDrawingClick(latlng) {
      // 调用store中的处理方法
      bridgeMapStore.actions.handleDrawingClick(latlng);
      
      // 同步本地状态
      this.lastClickLat = bridgeMapStore.state.lastClickLat;
      this.lastClickLng = bridgeMapStore.state.lastClickLng;
      this.drawingCoordinates = [...bridgeMapStore.state.drawingCoordinates];
      
      // 同步编辑状态
      if (bridgeMapStore.state.isEditingArea) {
        this.isEditingArea = true;
      }
      
      // 如果绘制完成，通知用户
      if (this.drawingCoordinates.length > 2 && !bridgeMapStore.state.isDrawing && bridgeMapStore.state.isEditingArea) {
        this.$message({
          message: '绘制完成，多边形已闭合',
          type: 'success',
          duration: 1500
        });
      }
    },
    
    // 计算两点间的距离 (直接使用mapUtils中的通用方法)
    calculateDistance(lat1, lng1, lat2, lng2) {
      return mapUtils.calculateDistance(lat1, lng1, lat2, lng2);
    },
    
    // 鼠标移动事件处理
    onMouseMove(event) {
      try {
        // 如果已经有节流函数在运行，跳过处理
        if (window._mouseMoveThrottled) return;
        
        // 设置节流标志，300ms后再次处理鼠标移动
        window._mouseMoveThrottled = true;
        setTimeout(() => {
          window._mouseMoveThrottled = false;
        }, 300);
        
        // 安全地处理各种格式的事件参数
        let coords;
        
        if (!event) {
          return; // 如果事件为空，直接返回
        }
        
        if (typeof event === 'object' && 'lat' in event && 'lng' in event) {
          // 直接是坐标对象
          coords = event;
        } else if (event.latlng && typeof event.latlng === 'object') {
          // 标准Leaflet事件对象
          coords = event.latlng;
        } else {
          // 尝试使用工具函数
          coords = mapUtils.extractCoordinatesFromMapEvent(event);
        }
        
        if (coords && typeof coords.lat !== 'undefined' && typeof coords.lng !== 'undefined') {
          this.mouseLat = coords.lat;
          this.mouseLng = coords.lng;
        }
      } catch (error) {
        // 鼠标移动错误不输出，避免控制台刷屏
        if (!window._mouseMoveErrorThrottled) {
          window._mouseMoveErrorThrottled = true;
          console.warn('处理鼠标移动事件失败');
          
          // 节流错误输出，每3秒允许输出一次
          setTimeout(() => {
            window._mouseMoveErrorThrottled = false;
          }, 3000);
        }
      }
    },
    
    // 处理地图缩放
    handleMapZoom(delta) {
      if (this.map) {
        const currentZoom = this.map.getZoom();
        this.map.setZoom(currentZoom + delta);
      }
    },
    
    // 重置地图视图
    resetMapView() {
      if (this.map) {
        // 如果有桥梁坐标，则定位到桥梁
        if (this.bridgeForm.latitude && this.bridgeForm.longitude) {
          this.map.setView([this.bridgeForm.latitude, this.bridgeForm.longitude], 15);
        } else {
          // 否则使用默认视图
          this.map.setView(this.center, 10);
        }
      }
    },
    
    // 切换全屏显示
    toggleFullscreen() {
      const mapContainer = document.querySelector('.map-content');
      if (mapContainer) {
        if (!document.fullscreenElement) {
          mapContainer.requestFullscreen().catch(err => {
            console.error(`全屏切换出错: ${err.message}`);
          });
        } else {
          document.exitFullscreen();
        }
      }
    },
    
    // 处理图层可见性变化
    handleLayerVisibilityChange({ layerId, isVisible }) {
      console.log(`图层 ${layerId} 可见性: ${isVisible}`);
      // 这里可以实现具体的图层控制逻辑
    },
    
    // 处理工具切换
    handleToolChange(tool) {
      console.log('工具切换:', tool);
      // 根据选择的工具执行相应操作
      if (tool === 'draw') {
        this.startDrawing(true);
      } else if (tool === 'location') {
        // 使用store管理位置拾取状态，不需要在本地保存状态
        bridgeMapStore.actions.toggleLocationPicking();
      }
    }
  }
}; 